import { env } from "../config/env";
import { createClient } from "./http";

const api = createClient(env.WAREHOUSE_BASE_URL);

function appendIfPresent(formData, key, value) {
  if (value === undefined || value === null || value === "") return;
  formData.append(key, value);
}

function normalizeArray(value) {
  return Array.isArray(value)
    ? value.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
}
    
function normalizeContexts(value) {
  const raw = normalizeArray(value).map((item) => String(item || "").trim().toUpperCase());
  const mapped = raw
    .map((item) => {
      if (["BOTH", "ALL", "GLOBAL"].includes(item)) return "ALL";
      if (["GROCERY", "GROCERIES", "MART", "STORE"].includes(item)) return "GROCERY";
      if (["FOOD", "RESTAURANT", "RESTAURANTS"].includes(item)) return "FOOD";
      return null;
    })
    .filter(Boolean);
  if (mapped.includes("ALL")) return ["ALL"];
  const unique = Array.from(new Set(mapped));
  return unique.length ? unique : ["GROCERY"];
}

function buildFilters(payload) {
  return {
    contexts: normalizeContexts(payload?.filters?.contexts),
    warehouseIds: normalizeArray(payload?.filters?.warehouseIds),
    cities: normalizeArray(payload?.filters?.cities),
    states: normalizeArray(payload?.filters?.states),
  };
}

function buildFormData(payload, { includeVideo = true } = {}) {
  const formData = new FormData();

  appendIfPresent(formData, "title", payload?.title?.trim());
  appendIfPresent(formData, "description", payload?.description?.trim());
  appendIfPresent(formData, "redirectType", payload?.redirectType || "PRODUCT");
  appendIfPresent(formData, "productId", payload?.productId?.trim());
  appendIfPresent(formData, "productVariantId", payload?.productVariantId?.trim());
  appendIfPresent(formData, "deepLink", payload?.deepLink?.trim());
  appendIfPresent(formData, "ctaLabel", payload?.ctaLabel?.trim());
  appendIfPresent(formData, "targetType", payload?.targetType || "ALL");
  appendIfPresent(formData, "displayOrder", payload?.displayOrder ?? 100);
  appendIfPresent(formData, "startDate", payload?.startDate);
  appendIfPresent(formData, "endDate", payload?.endDate);
  appendIfPresent(formData, "dailyStartTime", payload?.dailyStartTime || "00:00");
  appendIfPresent(formData, "dailyEndTime", payload?.dailyEndTime || "23:59");

  if (payload?.isActive !== undefined) {
    formData.append("isActive", String(payload.isActive));
  }

  formData.append("filters", JSON.stringify(buildFilters(payload)));
  formData.append("daysOfWeek", JSON.stringify(normalizeArray(payload?.daysOfWeek).length ? normalizeArray(payload.daysOfWeek) : ["0", "1", "2", "3", "4", "5", "6"]));

  if (includeVideo && payload?.video instanceof File) {
    formData.append("video", payload.video);
  }

  if (payload?.thumbnail instanceof File) {
    formData.append("thumbnail", payload.thumbnail);
  }

  return formData;
}

function buildJsonPayload(payload) {
  return {
    id: payload?.id || undefined,
    title: payload?.title?.trim(),
    description: payload?.description?.trim() || null,
    redirectType: payload?.redirectType || "PRODUCT",
    productId: payload?.productId?.trim() || null,
    productVariantId: payload?.productVariantId?.trim() || null,
    deepLink: payload?.deepLink?.trim() || null,
    ctaLabel: payload?.ctaLabel?.trim() || null,
    targetType: payload?.targetType || "ALL",
    filters: buildFilters(payload),
    displayOrder: Number(payload?.displayOrder ?? 100),
    isActive: Boolean(payload?.isActive ?? true),
    startDate: payload?.startDate,
    endDate: payload?.endDate,
    dailyStartTime: payload?.dailyStartTime || "00:00",
    dailyEndTime: payload?.dailyEndTime || "23:59",
    daysOfWeek: normalizeArray(payload?.daysOfWeek).length ? normalizeArray(payload.daysOfWeek) : ["0", "1", "2", "3", "4", "5", "6"],
  };
}

export const shortVideosService = {
  async list({ page = 1, limit = 10, q = "", isActive = "", status = "", context = "", includeDeleted = false } = {}) {
    return api.get("/api/admin/short-videos", {
      params: { page, limit, q, isActive, status, context, includeDeleted },
    });
  },

  async getById(id) {
    return api.get(`/api/admin/short-videos/${id}`);
  },

  async getFilterOptions(params = {}) {
    return api.get("/api/admin/short-videos/filter-options", { params });
  },

  async searchProductOptions({ q = "", warehouseId = "", warehouseIds = [], context = "", limit = 20 } = {}) {
    const normalizedWarehouseIds = Array.isArray(warehouseIds)
      ? warehouseIds.map((item) => String(item || "").trim()).filter(Boolean)
      : String(warehouseIds || "").split(",").map((item) => item.trim()).filter(Boolean);

    return api.get("/api/admin/short-videos/product-options", {
      params: {
        q,
        warehouseId,
        warehouseIds: normalizedWarehouseIds.join(","),
        context,
        limit,
      },
    });
  },

  async getProductVariants(productId, { q = "", warehouseId = "", warehouseIds = [], productType = "", limit = 50 } = {}) {
    if (!productId) {
      return Promise.resolve({ data: { success: true, data: [] } });
    }

    const normalizedWarehouseIds = Array.isArray(warehouseIds)
      ? warehouseIds.map((item) => String(item || "").trim()).filter(Boolean)
      : String(warehouseIds || "").split(",").map((item) => item.trim()).filter(Boolean);

    return api.get(`/api/admin/short-videos/product-options/${productId}/variants`, {
      params: {
        q,
        warehouseId,
        warehouseIds: normalizedWarehouseIds.join(","),
        productType,
        limit,
      },
    });
  },

  async summary() {
    return api.get("/api/admin/short-videos/summary");
  },

  async conflicts(payload) {
    return api.post("/api/admin/short-videos/conflicts", buildJsonPayload(payload));
  },

  async create(payload) {
    return api.post("/api/admin/short-videos", buildFormData(payload));
  },

  async update(id, payload) {
    return api.patch(`/api/admin/short-videos/${id}`, buildFormData(payload, { includeVideo: true }));
  },

  async setStatus(id, isActive) {
    return api.patch(`/api/admin/short-videos/${id}/status`, { isActive });
  },

  async remove(id) {
    return api.del(`/api/admin/short-videos/${id}`);
  },

  async stats(id) {
    return api.get(`/api/admin/short-videos/${id}/stats`);
  },

  async history({ id = null, page = 1, limit = 50 } = {}) {
    if (id) return api.get(`/api/admin/short-videos/${id}/history`, { params: { page, limit } });
    return api.get("/api/admin/short-videos/history", { params: { page, limit } });
  },

  async deleteHistory({ videoId = null, historyId = null, beforeDate = "", action = "", confirm = true } = {}) {
    if (historyId) {
      return api.del(`/api/admin/short-videos/history/${historyId}`);
    }

    const params = new URLSearchParams();
    params.set("confirm", confirm ? "true" : "false");
    if (beforeDate) params.set("beforeDate", beforeDate);
    if (action) params.set("action", action);

    const query = params.toString();
    const path = videoId
      ? `/api/admin/short-videos/${videoId}/history`
      : "/api/admin/short-videos/history";

    return api.del(query ? `${path}?${query}` : path);
  },
};

export function buildShortVideoAssetUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = env.WAREHOUSE_BASE_URL || "";
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}
  